import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as action from 'store/member'
import _ from 'lodash'
import {
  Avatar,
  Box,
} from '@mui/material'
import CustomAvatar from 'components/Avatar'
import 'utils/global.scss'
import { useEmploymentType } from 'components/Providers'
import { ROWS_PER_PAGE_OPTIONS } from 'constants'
import { DataGrid } from '@mui/x-data-grid'
import { hasPermission } from 'store'

function MemberList() {
  const dispatch = useDispatch()
  const employmentType = useEmploymentType()
  const { list, count } = useSelector(state => state.member)
  const query = useSelector(state => state.member.query[employmentType])
  const navigate = useNavigate()
  const { t } = useTranslation()
  const pageSizeOptions = _.uniq([...ROWS_PER_PAGE_OPTIONS])

  const paginationModel = {
    page: query?.limit ? query?.offset / query?.limit : 0,
    pageSize: query?.limit || 10,
  }

  const canOpenProfile = hasPermission(employmentType === 'student' ? 'student.profile' : 'staff.profile')

  const openDetailPage = (params) => {
    const { id } = params.row
    const type = params.row?.person?.type
    const url = ['/member', type, id].join('/')
    navigate(url)
  }

  const handlePaginationModelChange = (newModel) => {
    const { page, pageSize } = newModel
    if (pageSize !== paginationModel.pageSize) {
      dispatch(action.search({ ...query, type: employmentType, limit: pageSize, offset: 0 }))
    } else {
      dispatch(action.search({ ...query, type: employmentType, limit: pageSize, offset: pageSize * page }))
    }
  }

  const tableColumns = [
    {
      field: 'fullName',
      headerName: t('Full Name'),
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params) =>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{
              '&:hover': { transform: 'scale(2)', zIndex: 100 },
              transition: 'transform 0.2s ease-in-out',
              transform: 'scale(1)',
              width: 50,
              height: 50,
            }}
            src={params?.row?.person?.profilePicture?.location}
            {...CustomAvatar(`${params?.row?.person?.firstName} ${params?.row?.person?.lastName}`)}
          >
            {[params?.row?.person?.firstName?.[0], params?.row?.person?.lastName?.[0]].join('')}
          </Avatar>
          <Box sx={{ marginLeft: 1 }}>
            {`${params?.row?.person?.firstName} ${params?.row?.person?.lastName}`}
          </Box>
        </Box>
    },
    {
      field: 'localId',
      headerName: employmentType === 'staff' ? t('Staff ID') : t('Student ID'),
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: params => params?.row?.person?.localId || '',
    },
    employmentType === 'staff' ? {
      field: 'email',
      headerName: t('Email'),
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: params => params?.row?.person?.email || '',
    } : {
      field: 'gradeSection',
      headerName: t('Grade Section'),
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: params => {
        const enrollment = params?.row?.academicYearEnrollment;
        return `${enrollment?.section?.gradeSection ? `${enrollment?.section?.gradeSection}` : ''}`;
      }
    },
    employmentType === 'staff' ? {
      field: 'phoneExtension',
      headerName: t('Phone Extension'),
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: params => params?.row?.person?.phoneExtension || '',
    } : {
      field: 'email',
      headerName: t('Email'),
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: params => params?.row?.person?.email || '',
    },
    employmentType === 'staff' ? {
      field: 'jobTitle',
      headerName: t('Job Title'),
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: params => params?.row?.person?.jobTitle?.name || '',
    } : null,
  ].filter(Boolean)

  return (
    <Box px={2} py={1}>
      <DataGrid
        rows={list || []}
        columns={tableColumns}
        onPaginationModelChange={handlePaginationModelChange}
        onRowClick={canOpenProfile ? openDetailPage : undefined}
        pageSizeOptions={pageSizeOptions}
        paginationModel={paginationModel}
        paginationMode="server"
        rowCount={count || 0}
        sx={{
          height: 'calc(100vh - 223px)',
          overflow: 'auto',
          '& .MuiDataGrid-row:hover': { cursor: canOpenProfile ? 'pointer' : 'default' },
        }}
        pagination
        disableColumnMenu
      />
    </Box>
  )
}

export default MemberList
